// Desktop Chromium (Windows/Linux) reports HEVC as supported through
// MediaSource.isTypeSupported even with no hardware decoder, so hls.js keeps the
// HEVC rungs, the ABR climbs into one, and software decode stutters on every
// ~2s CMAF segment. MediaCapabilities.decodingInfo tells them apart: it reports
// `powerEfficient` (hardware) vs software fallback. When HEVC is not smoothly
// hardware-decodable, we drop those rungs so the ABR stays on the H.264 ones
// the v8 ladder always carries.

export interface CodecLevel {
	videoCodec?: string;
	width?: number;
	height?: number;
	bitrate?: number;
}

export type DecodingInfoFn = (
	config: MediaDecodingConfiguration
) => Promise<MediaCapabilitiesDecodingInfo>;

const HEVC_CODEC = /^(hvc1|hev1)\b/i;

export function isHevcLevel(level: CodecLevel): boolean {
	return !!level.videoCodec && HEVC_CODEC.test(level.videoCodec);
}

/**
 * Indices of the HEVC levels the device cannot decode smoothly in hardware, so
 * the caller can remove them. Guarantees:
 * - H.264 levels are never touched.
 * - Nothing is removed when the API is missing or throws (no regression on
 *   uncertainty — a working HEVC path must not be cut on a maybe).
 * - Never removes every level (a stuttering HEVC still beats a dead player), and
 *   only acts when at least one non-HEVC rung remains to fall back to.
 */
export async function hevcLevelsToRemove(
	levels: ReadonlyArray<CodecLevel>,
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<number[]> {
	if (!decodingInfo) return [];
	const hevc = levels
		.map((level, index) => ({ level, index }))
		.filter(entry => isHevcLevel(entry.level));
	if (hevc.length === 0) return [];
	// No H.264 rung to fall back to — leave the ladder alone.
	if (levels.length - hevc.length === 0) return [];

	// All rungs probed at once: the window between MANIFEST_PARSED and the
	// removal is exactly when a video switch can invalidate the result, so it
	// should be one round trip, not one per rung.
	const infos = await Promise.all(
		hevc.map(
			async ({ level }): Promise<MediaCapabilitiesDecodingInfo | undefined> => {
				try {
					return await decodingInfo({
						type: 'media-source',
						video: {
							contentType: `video/mp4; codecs="${level.videoCodec}"`,
							width: level.width ?? 1920,
							height: level.height ?? 1080,
							bitrate: level.bitrate ?? 2_000_000,
							framerate: 30
						}
					});
				} catch {
					// Fail-open on uncertainty: API missing or throwing shouldn't break HEVC
					return undefined;
				}
			}
		)
	);
	const remove: number[] = [];
	hevc.forEach(({ index }, i) => {
		const info = infos[i];
		// Keep on uncertainty; remove only when the platform confirms it can't
		// decode this rung smoothly in hardware.
		if (info && (!info.supported || !info.smooth || !info.powerEfficient)) {
			remove.push(index);
		}
	});

	if (remove.length >= levels.length) return [];
	return remove;
}

/** What survives a ladder mutation: hls.js never rewrites these on a rung. */
export function levelIdentity(level: CodecLevel): string {
	return `${level.videoCodec ?? ''}|${level.height ?? ''}|${level.bitrate ?? ''}`;
}

/**
 * Maps indices computed against a pre-await snapshot onto the live ladder.
 * hls.js can drop a rung on its own within the same load (codec or key error),
 * so a snapshot index may now name a different rung. Targets are matched by
 * identity; ones already gone are skipped; and the result is never the whole
 * live ladder, so a caller cannot strip everything whatever moved underneath.
 */
export function resolveLevelIndices(
	snapshot: ReadonlyArray<CodecLevel>,
	indices: ReadonlyArray<number>,
	live: ReadonlyArray<CodecLevel>
): number[] {
	const targets = new Set(
		indices.flatMap(i => {
			const level = snapshot[i];
			return level ? [levelIdentity(level)] : [];
		})
	);
	const resolved = live
		.map((level, i) => (targets.has(levelIdentity(level)) ? i : -1))
		.filter(i => i >= 0);
	return resolved.length >= live.length ? [] : resolved;
}

/**
 * Returns whether the ladder contains HEVC rungs, indicating hardware HEVC
 * support survived the gating phase.
 */
export function hasHevcLevels(levels: ReadonlyArray<CodecLevel>): boolean {
	return levels.some(level => isHevcLevel(level));
}

/**
 * Pre-checks if the device can hardware-decode a representative HEVC config
 * (1080p30 2mbps hvc1) before the Hls instance is created. This determines
 * whether to set `videoPreference: { videoCodec: 'hvc1' }` in the constructor,
 * guiding initial selection toward HEVC per hls.js v1.7.0 API.md:
 * "videoPreference limits initial level selection when a baseline variant exists."
 *
 * Returns true (HW-OK) when MediaCapabilities confirms supported+smooth+powerEfficient,
 * false otherwise. Fail-open on API absence or throw: uncertainty should not break
 * an otherwise working HEVC path, so the caller may still attempt HEVC and rely on
 * the post-parse removeLevel gating to strip unplayable rungs.
 *
 * @see https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
 */
export async function canHardwareDecodeHevc(
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<boolean> {
	if (!decodingInfo) return true;
	try {
		const info = await decodingInfo({
			type: 'media-source',
			video: {
				contentType: 'video/mp4; codecs="hvc1.1.6.L120.90"',
				width: 1920,
				height: 1080,
				bitrate: 2_000_000,
				framerate: 30
			}
		});
		return info.supported && info.smooth && info.powerEfficient;
	} catch {
		return true;
	}
}
